<script lang="ts" setup>
import ConnectWalletBtn from '@/components/atoms/ConnectWalletBtn.vue';
import { useWallet } from 'solana-wallets-vue';
import { ref, watch } from 'vue';
import InvitationSummary from './InvitationSummary.vue';
import Success from './Success.vue';
import { useSolBalance } from '@/helpers/solana/useSolBalance';
import Pendings from './Pendings.vue';
import { provide } from 'vue';
import usePendings from './usePendings';
import { computed } from 'vue';
import { PendingInvite } from '@/types';
import USDValue from '@/components/molecules/USDValue.vue';
import Tickets from './Tickets.vue';
import { onMounted } from 'vue';
import Winners from '../Winners.vue';

const { connected, publicKey, select, connect } = useWallet();
const value = ref<number | null>(null);
const showSuccess = ref<boolean>(false);
const successData = ref<PendingInvite | null>(null);
const showInvitationSummary = ref(false);

const { solBalance } = useSolBalance();

onMounted(() => {
  localStorage.setItem('open-wallet-previously-connected', 'Jupiter');
  select('Jupiter' as any);
});

const pendings = usePendings();
provide('pendings', pendings);

const handleInvitationSuccess = (invite: PendingInvite) => {
  showInvitationSummary.value = false;
  successData.value = invite;
  showSuccess.value = true;
  pendings.refetch();
};

const handlePendingClick = (data: PendingInvite) => {
  successData.value = data;
  showSuccess.value = true;
};

const hasError = computed(() => {
  if (value.value != null && value.value < 0.01)
    return 'Enter at least 0.01 SOL';
  if (
    value.value != null &&
    solBalance.value != null &&
    value.value > solBalance.value
  )
    return 'Insufficient SOL balance';
  return null;
});

watch(publicKey, async (newPublicKey, oldPublicKey) => {
  if (oldPublicKey !== newPublicKey) {
    pendings.refetch();
  }
});
</script>

<template>
  <v-dialog max-width="400px" v-model="showInvitationSummary">
    <InvitationSummary
      :amount="value"
      @cancel="showInvitationSummary = false"
      @success="handleInvitationSuccess"
    />
  </v-dialog>
  <v-dialog max-width="500px" v-model="showSuccess">
    <Success :data="successData!" @close="showSuccess = false" />
  </v-dialog>
  <v-container
    class="flex flex-column items-center justify-center h-full gap-4 max-w-[500px]"
  >
    <!-- <section
      class="flex flex-column items-center justify-center gap-2 p-5 bg-surface/50 rounded-jup border"
    >
      <p class="font-extrabold text-primary text-center text-xl">
        Try to win $100k worth of Prizes!
      </p>
      <Tickets />
      <p class="text-center">
        Every time you invite 3 unique friends to become active users of Jupiter
        Mobile, you get 1 Lucky Draw Ticket.
      </p>
      <div
        class="flex flex-row items-center gap-2 text-disabled w-full my-2 text-sm"
      >
        <ul class="flex-1 border-r text-center border-r-gray-500 pr-4">
          <li class="whitespace-nowrap">1 ticket to receive $25,000</li>
          <li class="whitespace-nowrap">4 tickets to receive $12,500</li>
          <li class="whitespace-nowrap">5 tickets to receive $5,000</li>
        </ul>
        <p class="max-w-[150px] text-center pl-2">
          Contest will end on Jan 30th.
        </p>
      </div>
      <p class="text-center text-disabled text-xs">
        * The number of ticket is an estimation, Jupiter will run extra
        verification at the end of the competition to ensure claims were done by
        real new active users.
      </p>
    </section> -->
    <Winners />
    <Pendings v-if="connected" @click="handlePendingClick" />
    <h1 class="text-2xl font-extrabold text-primary text-center">
      Invite more friends to Solana!
    </h1>
    <section
      class="bg-primary p-6 rounded-jup flex flex-column justify-center items-center gap-4 w-full"
    >
      <div class="flex flex-column items-center justify-center gap-2">
        <!-- <h1
          class="text-xl flex sm:flex-row flex-col justify-center items-center gap-2"
        >

          <p class="text-center">Choose the size of your gift</p>
        </h1> -->
        <img src="/invites-cat.svg" class="w-7 h-7" />
        <p class="text-base text-center">
          Select the amount you wish to gift to your friend!
        </p>
      </div>

      <div class="flex flex-col items-center gap-2">
        <div>
          <div class="flex flex-row items-center gap-2">
            <input
              type="number"
              autofocus
              class="text-3xl font-bold text-right placeholder:text-surface/30 min-w-[1ch] border-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none flex-1 focus-within:outline-none"
              v-model="value"
              placeholder="0"
              min="0"
              :style="{
                width: value ? `calc(${value.toString().length}ch)` : '2ch',
              }"
              step="0.01"
            />
            <span class="font-bold text-3xl">SOL</span>
          </div>
        </div>
        <USDValue :value="value" class="opacity-50" />
      </div>
      <div
        class="text-sm flex flex-row items-center gap-2 py-1 px-2 rounded-full opacity-100 bg-black/20 text-white"
        v-if="solBalance != null"
      >
        <v-icon size="16">mdi-wallet-outline</v-icon>
        <p class="font-medium">{{ solBalance.toFixed(5) }} SOL</p>
      </div>

      <div class="flex flex-row items-center gap-2 mb-2">
        <v-btn
          size="small"
          variant="elevated"
          @click="value = 0.02"
          color="background"
          class="opacity-70"
          >0.02 SOL</v-btn
        >
        <v-btn
          size="small"
          variant="elevated"
          color="background"
          class="opacity-70"
          @click="value = 0.05"
          >0.05 SOL</v-btn
        >
        <v-btn
          size="small"
          variant="elevated"
          color="background"
          class="opacity-70"
          @click="value = 0.1"
          >0.1 SOL</v-btn
        >
      </div>

      <!-- Connect -->
      <ConnectWalletBtn v-if="!connected" />
      <v-btn v-else-if="hasError" disabled color="background" size="large">{{
        hasError
      }}</v-btn>
      <v-btn
        v-else
        :disabled="value == null"
        @click="showInvitationSummary = true"
        color="background"
        size="large"
        :variant="value == null ? 'tonal' : 'elevated'"
        primary
        >Create invite</v-btn
      >
    </section>
  </v-container>
</template>
